import React from "react";

// "Sign in to see": what a signed-out visitor sees in place of a figure that is only for signed-in
// users (never XXXX). Opens the sign-in dialog: the navbar listens for this event, so it works on
// any page without passing state around. Styles: .fn-lock and .fn-lockbox in src/styles/foundation.css.
export const OPEN_LOGIN_EVENT = "bi:open-login";

export const openLogin = () => {
  if (typeof window !== "undefined") window.dispatchEvent(new CustomEvent(OPEN_LOGIN_EVENT));
};

export const LockIcon = ({ size = 14 }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"
    strokeLinecap="round" strokeLinejoin="round" aria-hidden="true" focusable="false">
    <rect x="5" y="11" width="14" height="10" rx="2" />
    <path d="M8 11V7a4 4 0 0 1 8 0v4" />
  </svg>
);

// what: the hidden figure, for screen readers ("CAGR" -> "Sign in to see CAGR")
// href: a link instead of the sign-in dialog (the subscriber lock goes to /pricing)
// variant: "link" (default: blue words with the icon, in place of a figure) or "button" (the kit's secondary
//   button, for a lock that stands alone in a card, e.g. "Sign in to see how Focus360 Velocity did"; its
//   words may wrap)
const Locked = ({ what, label = "Sign in to see", onClick, href, className = "", variant = "link" }) => {
  const cls = `fn-lock${variant === "button" ? " fn-lock-btn" : ""}${className ? ` ${className}` : ""}`;
  // The label has its own span so a narrow table can show the icon alone (the words stay for screen
  // readers): put .fn-lock-icononly on the table, or .fn-lock-icononly-narrow for its narrow container
  // only (src/styles/foundation.css).
  const body = (
    <>
      <LockIcon size={variant === "button" ? 16 : 14} />
      <span className="fn-lock-label">{label}</span>
      {what && <span className="fn-sr"> {what}</span>}
    </>
  );
  return href ? (
    <a className={cls} href={href}>
      {body}
    </a>
  ) : (
    <button type="button" className={cls} onClick={onClick || openLogin}>
      {body}
    </button>
  );
};

export default Locked;

// A stock name the API hides from this visitor arrives as "XXXX": show a lock instead. Signing in
// is not what unlocks a name (the API sends names to subscribers only), so the lock reads
// "Subscribe to see" (to /pricing) for every visitor: "Sign in to see" would promise a name that
// signing in does not show. (Callers may still pass signedIn; it no longer changes the lock.)
export const nameOrLock = (name, { what = "stock name" } = {}) => {
  if (name !== "XXXX") return name;
  return <Locked what={what} label="Subscribe to see" href="/pricing" />;
};

// The lock box of the mocks (Details.dc.html, TradeIdeas.dc.html, ResearchAnswer.dc.html): a dashed panel
// with a 44px lock circle, a title, one line of text and one action. It stands in for a table or figure
// the viewer may not see and says nothing of what is behind it (no count, no names, no prices).
//   as: the title's element (h3 by default; h4 inside a card whose own heading is an h3)
//   textWidth: the text's maximum width in px, as the mock draws it (440 by default)
//   action: one action, e.g. <SignInButton />, a <Locked variant="button" /> or the page's own link
export const LockBox = ({ title, children, action, as: Heading = "h3", textWidth, className = "" }) => (
  <div className={`fn-lockbox${className ? ` ${className}` : ""}`}>
    <span className="fn-lockbox-icon" aria-hidden="true">
      <LockIcon size={20} />
    </span>
    {title && <Heading className="fn-lockbox-title">{title}</Heading>}
    {children && (
      <p className="fn-lockbox-text" style={textWidth ? { maxWidth: `${textWidth}px` } : undefined}>
        {children}
      </p>
    )}
    {action}
  </div>
);

// The lock box's sign-in action. primary: the filled button ("Log in or create a free account" cards);
// otherwise the secondary one (Trade Ideas: "Sign in").
export const SignInButton = ({ children = "Sign in", primary = false }) => (
  <button type="button" className={`fn-btn ${primary ? "fn-btn-primary" : "fn-btn-secondary"} fn-lockbox-action`} onClick={openLogin}>
    {children}
  </button>
);

// The sign-in lock card (Details.dc.html, TradeIdeas.dc.html): a lock box for a table or figure that is for
// signed-in members only (SEBI: returns only after sign-in), with "Log in or create a free account".
// subscribe: the subscriber lock instead, for what only subscribers see ("Live holdings are locked"): its
//   action is "Subscribe", to /pricing (or the path given). onSubscribe(path), e.g. react-router's navigate,
//   moves there without a page load.
// action: any other single action in place of either.
export const SignInCard = ({ title, children, as = "h3", className = "", textWidth, subscribe = false, onSubscribe, action }) => {
  let act = action;
  if (!act && subscribe) {
    const to = typeof subscribe === "string" ? subscribe : "/pricing";
    act = (
      <a
        className="fn-btn fn-btn-primary fn-lockbox-action"
        href={to}
        onClick={
          onSubscribe
            ? (e) => {
                e.preventDefault();
                onSubscribe(to);
              }
            : undefined
        }
      >
        Subscribe
      </a>
    );
  }
  if (!act) act = <SignInButton primary>Log in or create a free account</SignInButton>;
  return (
    <LockBox title={title} as={as} className={className} textWidth={textWidth} action={act}>
      {children}
    </LockBox>
  );
};
